import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useReportPage } from 'shared/hooks';
import { InfoTooltip } from 'ui';

import { green, grey, red } from '@mui/material/colors';
import { theme as colors } from '../../theme';

const useMainColumnStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  container: {
    height: '100%',
  },
  footerContainer: {
    width: '100%',
  },
  footer: {
    backgroundColor: theme.palette.error.dark,
  },
  valueGridItem: {
    marginTop: 'auto',
    marginBottom: 'auto',
  },
}));

const footerColorToHexMap = {
  green,
  white: '#fff',
  red,
};

export const ScoreWidgetMainColumn = ({
  label,
  preliminary,
  value,
  suffix,
  footer,
  footerColor,
  className,
  accent,
}) => {
  const classes = useMainColumnStyles();
  const { orangeAccent } = colors.palette.custom;
  const rootClasses = clsx(classes.root, className);
  return (
    <>
      <Grid item xs className={rootClasses}>
        <Grid
          className={classes.container}
          container
          direction="column"
          align="center"
          justifyContent="space-between"
        >
          <Grid item>
            <Box pt={2}>
              <Typography variant="subtitle2">{label}</Typography>
            </Box>
          </Grid>
          <Grid item className={classes.valueGridItem}>
            <Box>
              <Typography variant="h3" color={accent ? orangeAccent : ''}>
                {preliminary ? '(P) ' : ''}
                {value}
                {suffix ? <sub style={{ fontSize: '0.5em' }}>{suffix}</sub> : ''}
              </Typography>
            </Box>
          </Grid>
          {footer && (
            <Grid item className={classes.footerContainer}>
              <Box
                p={1}
                className={classes.footer}
                style={{ backgroundColor: footerColorToHexMap[footerColor] }}
              >
                <Typography
                  display="block"
                  variant="button"
                  align="center"
                  color={footerColor === 'white' ? 'primary' : undefined}
                >
                  <strong>{footer}</strong>
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};

ScoreWidgetMainColumn.defaultProps = {
  footer: null,
  footerColor: null,
  className: undefined,
  preliminary: false,
  suffix: null,
};

ScoreWidgetMainColumn.propTypes = {
  preliminary: PropTypes.bool,
  suffix: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  footer: PropTypes.string,
  footerColor: PropTypes.oneOf(['green', 'white', 'red']),
};

const useColumnStyles = makeStyles({
  root: {
    minHeight: useReportPage ? 115 : 150,
  },
  fullHeight: {
    height: '100%',
  },
  columnInnerBoxWithBorder: {
    borderRight: `2px solid ${grey[400]}`,
  },
  columnValue: {
    margin: 'auto 0',
  },
});

const ScoreWidgetColumn = ({ children, label, tooltip, last }) => {
  const classes = useColumnStyles();

  return (
    <>
      <Grid item xs className={classes.root}>
        <Box py={2} className={classes.fullHeight}>
          <Box
            px={2}
            className={`${classes.fullHeight}${last ? '' : ` ${classes.columnInnerBoxWithBorder}`}`}
          >
            <Grid container direction="column" align="center" className={classes.fullHeight}>
              <Grid item>
                <Grid container justifyContent="center" spacing={1}>
                  <Grid item>
                    <Typography variant="subtitle2" color="primary">
                      {label}
                    </Typography>
                  </Grid>
                  {tooltip && (
                    <Grid item>
                      <InfoTooltip title={tooltip} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item className={classes.columnValue}>
                {children}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Grid>
    </>
  );
};

ScoreWidgetColumn.defaultProps = {
  last: false,
  tooltip: null,
  accent: false,
};

ScoreWidgetColumn.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.node.isRequired,
  tooltip: PropTypes.string,
  last: PropTypes.bool,
  accent: PropTypes.bool,
};

export const ScoreWidgetSingleValueColumn = ({ label, value, subvalue, tooltip, last }) => (
  <ScoreWidgetColumn label={label} last={last} tooltip={tooltip}>
    <Typography variant="h5">{value}</Typography>
    {subvalue && <Typography variant="subtitle1">{subvalue}</Typography>}
  </ScoreWidgetColumn>
);

ScoreWidgetSingleValueColumn.defaultProps = {
  last: false,
  subvalue: null,
  tooltip: null,
};

ScoreWidgetSingleValueColumn.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subvalue: PropTypes.string,
  tooltip: PropTypes.string,
  last: PropTypes.bool,
};

export const ScoreWidgetMultiValuesColumn = ({ label, values, last }) => (
  <ScoreWidgetColumn label={label} last={last}>
    {values.map((value) => (
      <Box key={value.label} mb={values.indexOf(value) + 1 === values.length ? 1 : 0}>
        <Typography color="primary" variant="caption">
          <strong>{value.label}</strong>
        </Typography>
        <Typography variant="body2">{value.value}</Typography>
      </Box>
    ))}
  </ScoreWidgetColumn>
);

ScoreWidgetMultiValuesColumn.defaultProps = {
  last: false,
};

ScoreWidgetMultiValuesColumn.propTypes = {
  label: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  last: PropTypes.bool,
};

const useRootStyles = makeStyles({
  root: {
    overflow: 'hidden',
  },
});

export const ScoreWidget = ({ children }) => {
  const classes = useRootStyles();

  return (
    <Paper variant="outlined" elevation={0} className={classes.root}>
      <Grid container>{children}</Grid>
    </Paper>
  );
};

ScoreWidget.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node.isRequired).isRequired,
};
